import { env } from '@/env';

/**
 * Builds an iManage document deep link from an iManage document number.
 *
 * Port of the legacy `OpenIManageDB` routine: it normalizes the separators,
 * splits the number/version, derives the target iManage database from the
 * document number, and selects the docs vs. archive host accordingly.
 *
 * The docs/archive hosts are environment-specific and provided via env vars.
 *
 * @param docNum - The raw iManage document number (e.g. "60281077.3").
 * @returns The fully-qualified iManage URL, or an empty string when no
 *   document number is supplied.
 */
export const buildIManageUrl = (docNum: string | null | undefined): string => {
  const trimmed = docNum?.trim();
  if (!trimmed) return '';

  // Commas are thousands separators in the entered number and are stripped.
  // A version, when present, follows a '.' or '_' separator.
  const normalized = trimmed.replaceAll(',', '').replaceAll('_', '.');

  const indexOfPeriod = normalized.indexOf('.');
  let docNumberPart: string;
  let versionPart: string;
  if (indexOfPeriod >= 0) {
    docNumberPart = normalized.substring(0, indexOfPeriod);
    versionPart = normalized.substring(indexOfPeriod);
  } else {
    docNumberPart = normalized;
    versionPart = '.1';
  }

  const subSection = docNumberPart.substring(0, 1);
  const docNumLen = docNumberPart.length;

  // By default, redirect to the Archive database.
  let dbName = 'ARCHIVE';
  // Determine the DB name based on the length and first character of the number.
  if (docNumLen === 8 && ['6', '7', '8', '9'].includes(subSection)) dbName = 'DB1';
  if (docNumLen === 8 && (subSection === '2' || subSection === '3')) dbName = 'DB2';
  if (docNumLen === 9 && (subSection === '2' || subSection === '3')) dbName = 'DB3';
  if (subSection === '5') dbName = 'EU1';
  if (docNumLen === 9 && subSection === '1') dbName = 'DB1';

  // Archive documents live on a different host than active documents.
  const hostAndProtocol = dbName === 'ARCHIVE' ? env.imanageArchiveUrl : env.imanageDocsUrl;

  return `${hostAndProtocol}/work/link/d/${dbName}!${docNumberPart}${versionPart}`;
};
